import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, CheckCircle2, Mail, Loader2, Eye, Download } from "lucide-react";
import { Page, Reveal, SectionTag } from "@/components/Reveal";
import { CONTACT_EMAIL } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { key: "as_built", label: "As-built survey" },
  { key: "matterport", label: "Matterport 3D scan" },
  { key: "combined", label: "Survey + scan (best value)" },
  { key: "walkthrough", label: "Property walk-through" },
];

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  site_address: "",
  square_footage: "",
  services: [],
  deadline: "",
  notes: "",
};

const inputCls =
  "w-full rounded-sm border border-white/10 bg-surface px-4 py-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-cad/60";

const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400";

export default function Quote() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (key) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(key)
        ? f.services.filter((s) => s !== key)
        : [...f.services, key],
    }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.company.trim()) errs.company = "Company is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "A valid email is required";
    if (!form.site_address.trim()) errs.site_address = "Site address is required";
    if (!form.square_footage || Number(form.square_footage) <= 0)
      errs.square_footage = "Approximate square footage is required";
    if (form.services.length === 0) errs.services = "Select at least one service";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote-requests`, {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone || null,
        site_address: form.site_address,
        square_footage: Number(form.square_footage),
        services: form.services,
        deadline: form.deadline || null,
        notes: form.notes || null,
      });
      setResult(data);
      toast.success("Quote request sent — we'll be in touch soon.");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(
        err.response?.data?.detail?.[0]?.msg ||
          err.response?.data?.detail ||
          "Something went wrong sending your request. Please try again or email us directly."
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedLabels = SERVICE_OPTIONS.filter((s) => form.services.includes(s.key)).map(
    (s) => s.label
  );

  return (
    <Page>
      <section className="blueprint-grid relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-36 sm:px-6 lg:px-8 lg:pt-44">
          <Reveal>
            <SectionTag>Request a quote</SectionTag>
            <h1
              data-testid="quote-headline"
              className="max-w-4xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              Tell us about your site
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              We'll get back to you with pricing and available dates — typically within one
              business day. The more detail you share, the more accurate your quote will be.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-28 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {result ? (
              <motion.div
                data-testid="quote-success-panel"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="blueprint-grid rounded-md border border-precision/40 bg-surface p-8 sm:p-12"
              >
                <CheckCircle2 size={44} className="text-precision" />
                <h2 className="mt-6 font-display text-2xl font-bold text-slate-50 sm:text-3xl">
                  Request received
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                  {result.message}
                </p>
                <div className="mt-8 space-y-3 border-t border-white/10 pt-6 font-mono text-xs tracking-[0.1em]">
                  <p className="text-slate-400">
                    REF <span className="text-cad">{result.id.slice(0, 8).toUpperCase()}</span>
                  </p>
                  <p className="text-slate-400">
                    SERVICES{" "}
                    <span className="text-slate-300">{selectedLabels.join(" + ")}</span>
                  </p>
                  <p className="text-slate-400">
                    SITE <span className="text-slate-300">{form.site_address}</span>
                  </p>
                  <p className="text-slate-400">
                    AREA <span className="text-slate-300">{Number(form.square_footage).toLocaleString()} SQ FT</span>
                  </p>
                </div>
                <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  Quote typically within one business day · Files 7–10 days after site visit
                </p>
                <div
                  data-testid="success-sample-links"
                  className="mt-6 rounded-sm border border-cad/25 bg-canvas/60 p-5"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cad/80">
                    While you wait
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">
                    Review a real sample of our work — a full as-built survey drawing set
                    from an actual project:
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      href="/samples/sample-as-built-survey.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="success-sample-pdf-link"
                      className="flex items-center gap-2 rounded-sm border border-cad/40 bg-cad/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:bg-cad/20"
                    >
                      <Eye size={14} /> View sample PDF
                    </a>
                    <a
                      href="/samples/sample-as-built-survey.dwg"
                      download
                      data-testid="success-sample-dwg-link"
                      className="flex items-center gap-2 rounded-sm border border-cad/40 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:border-cad hover:bg-cad/10"
                    >
                      <Download size={14} /> DWG · 615 KB
                    </a>
                  </div>
                </div>
                <button
                  data-testid="quote-another-button"
                  onClick={() => {
                    setForm(EMPTY);
                    setResult(null);
                  }}
                  className="mt-8 rounded-sm border border-cad/40 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-cad transition-colors duration-200 hover:border-cad hover:bg-cad/10"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <Reveal>
                <form
                  data-testid="quote-form"
                  onSubmit={submit}
                  noValidate
                  className="rounded-md border border-white/10 bg-surface/50 p-6 sm:p-10"
                >
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="quote-name" className={labelCls}>Name *</label>
                      <input
                        id="quote-name"
                        data-testid="quote-name-input"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Smith"
                        className={inputCls}
                      />
                      {errors.name && <p data-testid="quote-name-error" className="mt-1.5 text-xs text-red-400">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="quote-company" className={labelCls}>Company *</label>
                      <input
                        id="quote-company"
                        data-testid="quote-company-input"
                        type="text"
                        value={form.company}
                        onChange={set("company")}
                        placeholder="Retail Group LLC"
                        className={inputCls}
                      />
                      {errors.company && <p data-testid="quote-company-error" className="mt-1.5 text-xs text-red-400">{errors.company}</p>}
                    </div>
                    <div>
                      <label htmlFor="quote-email" className={labelCls}>Email *</label>
                      <input
                        id="quote-email"
                        data-testid="quote-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jane@company.com"
                        className={inputCls}
                      />
                      {errors.email && <p data-testid="quote-email-error" className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="quote-phone" className={labelCls}>Phone</label>
                      <input
                        id="quote-phone"
                        data-testid="quote-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="(555) 123-4567"
                        className={inputCls}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="quote-address" className={labelCls}>Site address *</label>
                      <input
                        id="quote-address"
                        data-testid="quote-address-input"
                        type="text"
                        value={form.site_address}
                        onChange={set("site_address")}
                        placeholder="123 Main St, Suite 4, Austin, TX 78701"
                        className={inputCls}
                      />
                      {errors.site_address && <p data-testid="quote-address-error" className="mt-1.5 text-xs text-red-400">{errors.site_address}</p>}
                    </div>
                    <div>
                      <label htmlFor="quote-sqft" className={labelCls}>Approximate square footage *</label>
                      <input
                        id="quote-sqft"
                        data-testid="quote-sqft-input"
                        type="number"
                        min="1"
                        value={form.square_footage}
                        onChange={set("square_footage")}
                        placeholder="2400"
                        className={inputCls}
                      />
                      {errors.square_footage && <p data-testid="quote-sqft-error" className="mt-1.5 text-xs text-red-400">{errors.square_footage}</p>}
                    </div>
                    <div>
                      <label htmlFor="quote-deadline" className={labelCls}>Deadline for drawings</label>
                      <input
                        id="quote-deadline"
                        data-testid="quote-deadline-input"
                        type="date"
                        value={form.deadline}
                        onChange={set("deadline")}
                        className={inputCls}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <span className={labelCls}>Services needed *</span>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {SERVICE_OPTIONS.map((s) => {
                          const checked = form.services.includes(s.key);
                          return (
                            <label
                              key={s.key}
                              data-testid={`quote-service-${s.key.replace("_", "-")}`}
                              className={`flex cursor-pointer items-center gap-3 rounded-sm border px-4 py-3 transition-colors duration-200 ${
                                checked
                                  ? "border-cad/60 bg-cad/10"
                                  : "border-white/10 bg-surface hover:border-white/25"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="sr-only"
                                checked={checked}
                                onChange={() => toggleService(s.key)}
                              />
                              <span
                                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] border transition-colors duration-200 ${
                                  checked ? "border-cad bg-cad text-canvas" : "border-slate-600"
                                }`}
                              >
                                {checked && <Check size={12} strokeWidth={3.5} />}
                              </span>
                              <span className="text-sm text-slate-200">{s.label}</span>
                            </label>
                          );
                        })}
                      </div>
                      {errors.services && <p data-testid="quote-services-error" className="mt-1.5 text-xs text-red-400">{errors.services}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="quote-notes" className={labelCls}>Anything else we should know?</label>
                      <textarea
                        id="quote-notes"
                        data-testid="quote-notes-input"
                        rows={4}
                        value={form.notes}
                        onChange={set("notes")}
                        placeholder="Access notes, landlord contacts, multiple locations, tight timelines…"
                        className={`${inputCls} resize-y`}
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="quote-submit-button"
                    disabled={loading}
                    className="mt-8 flex w-full items-center justify-center gap-2 rounded-sm bg-laser px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-canvas shadow-[0_0_32px_rgba(245,158,11,0.3)] transition-colors duration-200 hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {loading && <Loader2 size={15} className="animate-spin" />}
                    {loading ? "Sending…" : "Request a Quote"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div data-testid="quote-sidebar" className="rounded-md border border-white/10 bg-surface p-7 lg:sticky lg:top-28">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-cad/80">Prefer email?</p>
                <a
                  data-testid="quote-email-link"
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="mt-3 flex items-center gap-3 text-sm text-slate-200 transition-colors hover:text-cad"
                >
                  <Mail size={16} className="text-laser" /> {CONTACT_EMAIL}
                </a>
                <div className="mt-8 border-t border-white/10 pt-7">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-cad/80">What happens next</p>
                  <ol className="mt-5 space-y-4">
                    {[
                      "We review your site details and scope.",
                      "You get a quote with pricing and available dates — typically within one business day.",
                      "We schedule the visit, measure in person, and deliver files in 7–10 days.",
                    ].map((t, i) => (
                      <li key={i} className="flex gap-4">
                        <span className="font-mono text-xs text-laser">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm leading-relaxed text-slate-400">{t}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="mt-8 border-t border-white/10 pt-7">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-cad/80">Your request</p>
                  <div className="mt-4 space-y-2 font-mono text-xs tracking-[0.08em]">
                    <p className="text-slate-400">
                      SERVICES{" "}
                      <span className="text-slate-300">
                        {selectedLabels.length ? selectedLabels.join(" + ") : "—"}
                      </span>
                    </p>
                    <p className="text-slate-400">
                      AREA{" "}
                      <span className="text-slate-300">
                        {form.square_footage ? `${Number(form.square_footage).toLocaleString()} SQ FT` : "—"}
                      </span>
                    </p>
                    <p className="text-slate-400">
                      DEADLINE <span className="text-slate-300">{form.deadline || "Flexible"}</span>
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Page>
  );
}
